import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { So5FixtureEvent, Sport } from '__generated__/globalTypes';
import { useSportContext } from 'contexts/sport';
import useQuery from 'hooks/graphql/useQuery';

import {
  NextSo5FixtureTeamSlugs,
  NextSo5FixtureTeamSlugsVariables,
  NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek,
} from './__generated__/useNextSo5FixtureTeamSlugs.graphql';

const teamFragment = gql`
  fragment NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek on TeamsPlayingNextGameWeek {
    slug
    gameWeek
    shortDisplayName
    endDate
    startDate
    teamSlugs
  }
` as TypedDocumentNode<NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek>;

const QUERY = gql`
  query NextSo5FixtureTeamSlugs(
    $isFootball: Boolean!
    $isBaseball: Boolean!
    $isBasketBall: Boolean!
    $eventType: So5FixtureEvent!
  ) {
    config {
      id
      so5 {
        id
        footballTeamsPlayingNextFixture: teamsPlayingNextGameWeeks(
          eventType: $eventType
          sport: FOOTBALL
        ) @include(if: $isFootball) {
          slug
          ...NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek
        }
        baseballTeamsPlayingNextFixture: teamsPlayingNextGameWeeks(
          eventType: $eventType
          sport: BASEBALL
        ) @include(if: $isBaseball) {
          slug
          ...NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek
        }
        nbaTeamsPlayingNextFixture: teamsPlayingNextGameWeeks(
          eventType: $eventType
          sport: NBA
        ) @include(if: $isBasketBall) {
          slug
          ...NextSo5FixtureTeamSlugs_TeamsPlayingNextGameWeek
        }
      }
    }
  }
  ${teamFragment}
` as TypedDocumentNode<
  NextSo5FixtureTeamSlugs,
  NextSo5FixtureTeamSlugsVariables
>;

interface Props {
  eventType?: So5FixtureEvent;
}

export default ({ eventType = So5FixtureEvent.CLASSIC }: Props) => {
  const { sport = Sport.FOOTBALL } = useSportContext();
  const { data, loading } = useQuery(QUERY, {
    variables: {
      eventType,
      isFootball: sport === Sport.FOOTBALL,
      isBaseball: sport === Sport.BASEBALL,
      isBasketBall: sport === Sport.NBA,
    },
    fetchPolicy: 'cache-first',
  });
  const teamsPlayingNextFixture = useMemo(() => {
    switch (sport) {
      case Sport.FOOTBALL:
        return data?.config?.so5?.footballTeamsPlayingNextFixture;
      case Sport.BASEBALL:
        return data?.config?.so5?.baseballTeamsPlayingNextFixture;
      case Sport.NBA:
        return data?.config?.so5?.nbaTeamsPlayingNextFixture;
      default:
        return [];
    }
  }, [data, sport]);

  return {
    teamsPlayingNextFixture: teamsPlayingNextFixture || [],
    loading,
  };
};
